import React, { useMemo } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

const parseAmount = (value) => {
  if (value == null || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const cleaned = String(value).replace(/[^\d.-]/g, '');
  if (!cleaned || cleaned === '-' || cleaned === '.') return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
};

const firstRow = (rows) => (Array.isArray(rows) && rows.length > 0 ? rows[0] : null);

const displayOf = (value) => {
  if (value == null || value === '') return '—';
  return String(value).trim();
};

/** Estimate metric column width from the longest formatted amount string. */
const estimateMetricColWidth = (values) => {
  const maxLen = values.reduce((max, v) => {
    const len = displayOf(v).length;
    return len > max ? len : max;
  }, 6);
  // ~7.4px per character for tabular-nums at ~0.8rem, plus horizontal padding.
  return Math.max(92, Math.min(148, Math.ceil(maxLen * 7.4 + 20)));
};

const fontSizeFor = (display, emphasize) => {
  const len = display.length;
  if (len >= 14) return emphasize ? '0.72rem' : '0.68rem';
  if (len >= 12) return emphasize ? '0.78rem' : '0.72rem';
  if (len >= 10) return emphasize ? '0.84rem' : '0.78rem';
  return emphasize ? '0.92rem' : '0.84rem';
};

const MetricCell = ({
  label,
  value,
  emphasize = false,
  muted = false,
  colWidth,
}) => {
  const num = parseAmount(value);
  const isNeg = num != null && num < 0;
  const isZero = num === 0;
  const display = displayOf(value);

  return (
    <Box
      sx={{
        width: colWidth,
        flex: `0 0 ${colWidth}px`,
        minWidth: colWidth,
        maxWidth: colWidth,
        px: 1.25,
        py: label ? 0.75 : 1.05,
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      {label ? (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            fontSize: '0.65rem',
            fontWeight: 600,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: muted ? 'rgba(15, 23, 42, 0.38)' : 'rgba(15, 23, 42, 0.48)',
            mb: 0.35,
            lineHeight: 1.2,
          }}
        >
          {label}
        </Typography>
      ) : null}
      <Typography
        sx={{
          fontSize: fontSizeFor(display, emphasize),
          fontWeight: emphasize ? 700 : 600,
          fontVariantNumeric: 'tabular-nums',
          letterSpacing: display.length >= 12 ? '-0.02em' : 0,
          color: isNeg
            ? '#C2410C'
            : isZero && muted
              ? 'rgba(15, 23, 42, 0.35)'
              : '#0F172A',
          lineHeight: 1.25,
          direction: 'ltr',
          unicodeBidi: 'isolate',
          whiteSpace: 'nowrap',
          maxWidth: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        title={display !== '—' ? display : undefined}
      >
        {display}
      </Typography>
    </Box>
  );
};

const SOURCE_COL_WIDTH = 104;

const SourceRow = ({
  label,
  accent,
  balance,
  credit,
  debit,
  loading = false,
  muted = false,
  isLast = false,
  metricColWidth,
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'stretch',
      borderBottom: isLast ? 'none' : '1px solid rgba(15, 23, 42, 0.06)',
      bgcolor: muted ? 'rgba(15, 23, 42, 0.02)' : 'transparent',
      minWidth: 'max-content',
    }}
  >
    <Box
      sx={{
        width: SOURCE_COL_WIDTH,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1.25,
        py: 1.1,
        borderInlineEnd: '1px solid rgba(15, 23, 42, 0.06)',
        borderInlineStart: `3px solid ${accent}`,
        bgcolor: muted ? 'transparent' : `${accent}14`,
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          bgcolor: accent,
          flexShrink: 0,
          opacity: muted ? 0.75 : 1,
        }}
      />
      <Typography
        sx={{
          fontSize: '0.78rem',
          fontWeight: 700,
          color: muted ? 'rgba(15, 23, 42, 0.55)' : accent,
          lineHeight: 1.2,
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Typography>
      {loading && <CircularProgress size={10} sx={{ color: accent, ml: 0.25 }} />}
    </Box>
    <Box sx={{ display: 'flex', flexShrink: 0 }}>
      <MetricCell
        label={null}
        value={balance}
        emphasize={!muted}
        muted={muted}
        colWidth={metricColWidth}
      />
      <Box sx={{ width: '1px', flexShrink: 0, bgcolor: 'rgba(15, 23, 42, 0.06)', my: 1 }} />
      <MetricCell label={null} value={credit} muted={muted} colWidth={metricColWidth} />
      <Box sx={{ width: '1px', flexShrink: 0, bgcolor: 'rgba(15, 23, 42, 0.06)', my: 1 }} />
      <MetricCell label={null} value={debit} muted={muted} colWidth={metricColWidth} />
    </Box>
  </Box>
);

/**
 * Compact Balance / Credit / Debit comparison for Xtram vs Finsite (when linked).
 * Column widths grow with large amounts so values stay separated and readable.
 */
const BankBalanceComparison = ({
  xtramRows = [],
  finsiteRows = [],
  diffRows = [],
  showFinsite = false,
  finsiteLoading = false,
  selectedRows = null,
  selectedCount = 0,
  width = 400,
}) => {
  const { t } = useTranslation();

  const xtram = useMemo(() => firstRow(xtramRows) || {}, [xtramRows]);
  const finsite = useMemo(() => firstRow(finsiteRows) || {}, [finsiteRows]);
  const diff = useMemo(() => firstRow(diffRows) || {}, [diffRows]);
  const selected = useMemo(() => firstRow(selectedRows) || {}, [selectedRows]);

  const showDiff = showFinsite && diffRows?.length > 0 && !finsiteLoading;
  const showSelected = selectedCount > 0 && selectedRows?.length > 0;

  const metricColWidth = useMemo(() => {
    const values = [
      xtram.balance,
      xtram.credit,
      xtram.debit,
    ];
    if (showFinsite) {
      values.push(finsite.balance, finsite.credit, finsite.debit);
    }
    if (showDiff) {
      values.push(diff.balance, diff.credit, diff.debit);
    }
    if (showSelected) {
      values.push(selected.balance, selected.credit, selected.debit);
    }
    return estimateMetricColWidth(values);
  }, [xtram, finsite, diff, selected, showFinsite, showDiff, showSelected]);

  const contentWidth = SOURCE_COL_WIDTH + metricColWidth * 3 + 2;
  const resolvedWidth = Math.max(width, contentWidth);

  return (
    <Box
      sx={{
        width: resolvedWidth,
        maxWidth: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      <Box
        sx={{
          borderRadius: '12px',
          border: '1px solid rgba(15, 23, 42, 0.08)',
          bgcolor: '#fff',
          overflowX: 'auto',
          overflowY: 'hidden',
          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 4px 12px rgba(15, 23, 42, 0.04)',
        }}
      >
        <Box sx={{ minWidth: contentWidth }}>
          {/* Column headers */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              bgcolor: '#E8951A',
              borderBottom: '1px solid rgba(26, 18, 5, 0.12)',
            }}
          >
            <Box
              sx={{
                width: SOURCE_COL_WIDTH,
                flexShrink: 0,
                px: 1.25,
                py: 1.1,
                borderInlineEnd: '1px solid rgba(26, 18, 5, 0.18)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxSizing: 'border-box',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.9rem',
                  fontWeight: 800,
                  color: '#1A1205',
                  letterSpacing: '0.01em',
                }}
              >
                {t('Source')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexShrink: 0 }}>
              {[t('Balance'), t('Credit'), t('Debit')].map((h, i) => (
                <Typography
                  key={h}
                  variant="caption"
                  sx={{
                    width: metricColWidth,
                    flex: `0 0 ${metricColWidth}px`,
                    textAlign: 'center',
                    fontSize: '0.9rem',
                    fontWeight: 800,
                    letterSpacing: '0.01em',
                    color: '#1A1205',
                    py: 1.1,
                    px: 0.5,
                    boxSizing: 'border-box',
                    borderInlineStart: i === 0 ? 'none' : '1px solid rgba(26, 18, 5, 0.18)',
                  }}
                >
                  {h}
                </Typography>
              ))}
            </Box>
          </Box>

          <SourceRow
            label={t('Xtram')}
            accent="#F9AA2A"
            balance={xtram.balance}
            credit={xtram.credit}
            debit={xtram.debit}
            metricColWidth={metricColWidth}
            isLast={!showFinsite && !showDiff}
          />

          {showFinsite && (
            <SourceRow
              label={t('Finsite')}
              accent="#0D9488"
              balance={finsite.balance}
              credit={finsite.credit}
              debit={finsite.debit}
              loading={finsiteLoading}
              metricColWidth={metricColWidth}
              isLast={!showDiff}
            />
          )}

          {showDiff && (
            <SourceRow
              label={t('Difference')}
              accent="#64748B"
              balance={diff.balance}
              credit={diff.credit}
              debit={diff.debit}
              muted
              metricColWidth={metricColWidth}
              isLast
            />
          )}
        </Box>
      </Box>

      {showSelected && (
        <Box
          sx={{
            borderRadius: '12px',
            border: '1px solid rgba(48, 79, 255, 0.18)',
            bgcolor: 'rgba(48, 79, 255, 0.03)',
            overflowX: 'auto',
            overflowY: 'hidden',
          }}
        >
          <Box sx={{ minWidth: metricColWidth * 3 + 2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 1.5,
                py: 0.65,
                borderBottom: '1px solid rgba(48, 79, 255, 0.12)',
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  color: '#304FFF',
                  letterSpacing: '0.02em',
                }}
              >
                {t('SelectedRows')}
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  color: 'rgba(48, 79, 255, 0.7)',
                  bgcolor: 'rgba(48, 79, 255, 0.1)',
                  px: 0.75,
                  py: 0.15,
                  borderRadius: '999px',
                }}
              >
                {selectedCount}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <MetricCell
                label={t('Balance')}
                value={selected.balance}
                emphasize
                colWidth={metricColWidth}
              />
              <Box sx={{ width: '1px', flexShrink: 0, bgcolor: 'rgba(48, 79, 255, 0.12)', my: 1 }} />
              <MetricCell label={t('Credit')} value={selected.credit} colWidth={metricColWidth} />
              <Box sx={{ width: '1px', flexShrink: 0, bgcolor: 'rgba(48, 79, 255, 0.12)', my: 1 }} />
              <MetricCell label={t('Debit')} value={selected.debit} colWidth={metricColWidth} />
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default BankBalanceComparison;
